import _objectSpread from '@babel/runtime/helpers/esm/objectSpread2';
import _slicedToArray from '@babel/runtime/helpers/esm/slicedToArray';
import { useDisclosure, Drawer, DrawerOverlay, DrawerContent, Flex, Stack, Box, Slide, Wrap, AspectRatio, Image } from '@chakra-ui/react';
import { Card } from '@outdoorly/card';
import { Divider } from '@outdoorly/divider';
import { NavLink, Header } from '@outdoorly/header';
import { ChevronRightIcon, XIcon, ArrowLeftIcon } from '@outdoorly/icons';
import { LogoWordmark } from '@outdoorly/logo';
import { Text } from '@outdoorly/text';
import propTypes from 'prop-types';
import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { useRouter } from 'next/router';
import { IconButton } from '@outdoorly/button';
import NextLink from 'next/link';
import { localizeUrl } from '@outdoorly/util';

var MainHeader = function MainHeader(_ref) {
  var onClose = _ref.onClose;
  return /*#__PURE__*/React.createElement(Box, {
    px: 4
  }, /*#__PURE__*/React.createElement(Stack, {
    direction: "row",
    justify: "space-between",
    align: "center",
    py: 4
  }, /*#__PURE__*/React.createElement(Box, {
    width: 6,
    height: 6
  }), /*#__PURE__*/React.createElement(LogoWordmark, {
    height: 8
  }), /*#__PURE__*/React.createElement(IconButton, {
    onClick: onClose,
    icon: /*#__PURE__*/React.createElement(XIcon, {
      width: 6,
      height: 6
    }),
    variant: "ghost"
  })), /*#__PURE__*/React.createElement(Divider, null));
};
MainHeader.propTypes = {
  onClose: propTypes.func
};
var NestedHeader = function NestedHeader(_ref2) {
  var onClose = _ref2.onClose,
    onBack = _ref2.onBack,
    header = _ref2.header;
  return /*#__PURE__*/React.createElement(Box, {
    px: 4
  }, /*#__PURE__*/React.createElement(Stack, {
    direction: "row",
    justify: "space-between",
    align: "center",
    py: 4
  }, /*#__PURE__*/React.createElement(IconButton, {
    onClick: onBack,
    icon: /*#__PURE__*/React.createElement(ArrowLeftIcon, {
      width: 6,
      height: 6
    }),
    variant: "ghost"
  }), /*#__PURE__*/React.createElement(Text, {
    fontWeight: "500",
    size: "2xl"
  }, header), /*#__PURE__*/React.createElement(IconButton, {
    onClick: onClose,
    icon: /*#__PURE__*/React.createElement(XIcon, {
      width: 6,
      height: 6
    }),
    variant: "ghost"
  })), /*#__PURE__*/React.createElement(Divider, null));
};
NestedHeader.propTypes = {
  onClose: propTypes.func,
  onBack: propTypes.func,
  header: propTypes.string.isRequired
};
var _NestedNavigator = function NestedNavigator(_ref3) {
  var _ref3$item = _ref3.item,
    item = _ref3$item === void 0 ? {
      children: []
    } : _ref3$item,
    isOpen = _ref3.isOpen,
    onBack = _ref3.onBack,
    onClose = _ref3.onClose;
  var _useDisclosure = useDisclosure(),
    childIsOpen = _useDisclosure.isOpen,
    closeChild = _useDisclosure.onClose,
    openChild = _useDisclosure.onOpen;
  var _useState = useState(undefined),
    _useState2 = _slicedToArray(_useState, 2),
    activeChild = _useState2[0],
    setActiveChild = _useState2[1];

  // MenuItems that have no children
  var plainChildren = useMemo(function () {
    var _item$children$filter;
    return (_item$children$filter = item.children.filter(function (child) {
      var _child$children;
      return !((_child$children = child.children) !== null && _child$children !== void 0 && _child$children.length);
    })) !== null && _item$children$filter !== void 0 ? _item$children$filter : [];
  }, [item]);

  // MenuItems that have items below them
  var childrenWithChildren = useMemo(function () {
    var _item$children$filter2;
    return (_item$children$filter2 = item.children.filter(function (child) {
      var _child$children2;
      return !!((_child$children2 = child.children) !== null && _child$children2 !== void 0 && _child$children2.length);
    })) !== null && _item$children$filter2 !== void 0 ? _item$children$filter2 : [];
  }, [item]);

  // MenuItems with images
  var childrenWithImages = useMemo(function () {
    var _item$children$filter3;
    return (_item$children$filter3 = item.children.filter(function (child) {
      return !!child.image;
    })) !== null && _item$children$filter3 !== void 0 ? _item$children$filter3 : [];
  }, [item]);
  return /*#__PURE__*/React.createElement(Slide, {
    timeout: 250,
    placement: "right",
    "in": isOpen,
    unmountOnExit: true
  }, /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Flex, {
    bg: "scheme.bgPrimary",
    direction: "column",
    h: "100%"
  }, /*#__PURE__*/React.createElement(NestedHeader, {
    onBack: onBack,
    onClose: onClose,
    header: item.name
  }), /*#__PURE__*/React.createElement(Stack, {
    p: 6,
    spacing: 5,
    overflowY: "scroll",
    flex: "auto"
  }, /*#__PURE__*/React.createElement(NavLink, {
    item: item.linked_object,
    textLink: true
  }, /*#__PURE__*/React.createElement(Text, {
    height: 6,
    fontWeight: "500",
    size: "2xl"
  }, "Shop all ", item.name)), childrenWithChildren.length && /*#__PURE__*/React.createElement(React.Fragment, null, childrenWithChildren.map(function (child) {
    return /*#__PURE__*/React.createElement(Flex, {
      key: child.id,
      direction: "row",
      justify: "space-between",
      onClick: function onClick() {
        setActiveChild(child);
        openChild();
      },
      _hover: {
        cursor: 'pointer'
      }
    }, /*#__PURE__*/React.createElement(Text, {
      fontWeight: "500",
      size: "2xl"
    }, child.name), /*#__PURE__*/React.createElement(ChevronRightIcon, {
      width: 6,
      height: 6
    }));
  }), /*#__PURE__*/React.createElement(Divider, {
    pt: 3,
    mb: 3
  })), plainChildren.map(function (child) {
    return /*#__PURE__*/React.createElement(NavLink, {
      key: child.id,
      item: child.linked_object,
      textLink: true
    }, /*#__PURE__*/React.createElement(Text, {
      size: "xl"
    }, child.name));
  }), /*#__PURE__*/React.createElement(Wrap, {
    justify: "space-around",
    pt: 6
  }, childrenWithImages.map(function (child) {
    var _child$image;
    return /*#__PURE__*/React.createElement(NavLink, {
      key: child.id,
      item: child.linked_object
    }, /*#__PURE__*/React.createElement(Stack, {
      as: "a",
      m: 2,
      spacing: 2,
      flex: 1,
      minWidth: 24,
      maxWidth: 40
    }, /*#__PURE__*/React.createElement(AspectRatio, {
      ratio: 2 / 3
    }, /*#__PURE__*/React.createElement(Card, {
      bg: "scheme.bgSecondary",
      padding: 0,
      overflow: "hidden"
    }, (child === null || child === void 0 || (_child$image = child.image) === null || _child$image === void 0 ? void 0 : _child$image.url) && /*#__PURE__*/React.createElement(Image, {
      src: localizeUrl(child.image.url),
      minHeight: "100%",
      minWidth: "100%",
      fit: "cover"
    }))), /*#__PURE__*/React.createElement(Text, {
      fontWeight: "500",
      size: "xl"
    }, child.name)));
  })), /*#__PURE__*/React.createElement(Box, {
    pb: 32
  }))), /*#__PURE__*/React.createElement(_NestedNavigator, {
    item: activeChild,
    isOpen: childIsOpen,
    onBack: closeChild,
    onClose: onClose
  })));
};
_NestedNavigator.propTypes = {
  item: propTypes.shape(_objectSpread(_objectSpread({}, NavLink.propTypes.item), {}, {
    name: propTypes.string.isRequired,
    children: propTypes.arrayOf(propTypes.shape(_objectSpread(_objectSpread({
      name: propTypes.string.isRequired
    }, NavLink.propTypes.item), {}, {
      image: propTypes.string,
      children: propTypes.arrayOf(propTypes.shape(_objectSpread(_objectSpread({
        name: propTypes.string.isRequired
      }, NavLink.propTypes.item), {}, {
        image: propTypes.string
      })))
    })))
  })),
  onClose: propTypes.func,
  onBack: propTypes.func,
  isOpen: propTypes.bool
};

/**
 * A menu to use on mobile. To be paired with `<MobileHeader />`.
 */
var MobileMenu = function MobileMenu(_ref4) {
  var _ref4$menuItems = _ref4.menuItems,
    menuItems = _ref4$menuItems === void 0 ? [] : _ref4$menuItems,
    _ref4$isOpen = _ref4.isOpen,
    isOpen = _ref4$isOpen === void 0 ? false : _ref4$isOpen,
    _ref4$onClose = _ref4.onClose,
    _onClose = _ref4$onClose === void 0 ? function () {} : _ref4$onClose,
    _ref4$isAuthed = _ref4.isAuthed,
    isAuthed = _ref4$isAuthed === void 0 ? false : _ref4$isAuthed,
    _ref4$onLogout = _ref4.onLogout,
    onLogout = _ref4$onLogout === void 0 ? function () {} : _ref4$onLogout;
  var _useDisclosure2 = useDisclosure(),
    childIsOpen = _useDisclosure2.isOpen,
    openChild = _useDisclosure2.onOpen,
    closeChild = _useDisclosure2.onClose;
  var router = useRouter();

  // When the URL changes, the mobile menu should close.
  var onRouteChange = useCallback(function () {
    _onClose();
    closeChild();
  }, [_onClose, closeChild]);
  useEffect(function () {
    router.events.on('routeChangeStart', onRouteChange);

    // If the component is unmounted, unsubscribe
    // from the event with the `off` method:
    return function () {
      router.events.off('routeChangeStart', onRouteChange);
    };
  }, [onRouteChange, router.events]);
  var _useState3 = useState(undefined),
    _useState4 = _slicedToArray(_useState3, 2),
    activeChild = _useState4[0],
    setActiveChild = _useState4[1];
  useEffect(function () {
    return function () {
      setActiveChild(undefined);
      _onClose();
    };
  }, [_onClose]);
  return /*#__PURE__*/React.createElement(Drawer, {
    placement: "right",
    onClose: function onClose() {
      _onClose();
      closeChild();
    },
    isOpen: isOpen,
    size: "sm"
  }, /*#__PURE__*/React.createElement(DrawerOverlay, null, /*#__PURE__*/React.createElement(DrawerContent, null, /*#__PURE__*/React.createElement(Flex, {
    bg: "scheme.bgPrimary",
    direction: "column",
    h: "100%"
  }, /*#__PURE__*/React.createElement(MainHeader, {
    onClose: _onClose
  }), /*#__PURE__*/React.createElement(Stack, {
    p: 6,
    spacing: 5,
    overflowY: "scroll"
  }, menuItems.map(function (item) {
    return /*#__PURE__*/React.createElement(Box, {
      key: item.id
    }, /*#__PURE__*/React.createElement(Flex, {
      direction: "row",
      justify: "space-between",
      onClick: function onClick() {
        setActiveChild(item);
        openChild();
      },
      _hover: {
        cursor: 'pointer'
      }
    }, /*#__PURE__*/React.createElement(Text, {
      fontWeight: "500",
      size: "2xl"
    }, item.name), /*#__PURE__*/React.createElement(ChevronRightIcon, {
      width: 6,
      height: 6
    })));
  }), /*#__PURE__*/React.createElement(Divider, {
    pt: 3,
    mb: 3
  }), isAuthed && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(NextLink, {
    passHref: true,
    href: "/profile/credits"
  }, /*#__PURE__*/React.createElement(Text, {
    as: "a",
    fontWeight: "500",
    size: "xl",
    _hover: {
      cursor: 'pointer'
    },
    role: "button",
    alignSelf: "flex-start"
  }, "Get $10 Credit")), /*#__PURE__*/React.createElement(NextLink, {
    passHref: true,
    href: "/profile"
  }, /*#__PURE__*/React.createElement(Text, {
    as: "a",
    fontWeight: "500",
    size: "xl",
    _hover: {
      cursor: 'pointer'
    },
    role: "button",
    alignSelf: "flex-start"
  }, "My Profile")), /*#__PURE__*/React.createElement(Text, {
    as: Box,
    fontWeight: "500",
    size: "xl",
    onClick: onLogout,
    _hover: {
      cursor: 'pointer'
    },
    role: "button",
    alignSelf: "flex-start"
  }, "Sign Out")), /*#__PURE__*/React.createElement(Box, {
    pb: 32
  }))), /*#__PURE__*/React.createElement(_NestedNavigator, {
    item: activeChild,
    isOpen: childIsOpen,
    onBack: closeChild,
    onClose: function onClose() {
      _onClose();
      closeChild();
    }
  }))));
};
MobileMenu.propTypes = {
  menuItems: Header.propTypes.menuItems,
  isOpen: propTypes.bool.isRequired,
  onClose: propTypes.func.isRequired,
  isAuthed: propTypes.bool.isRequired,
  onLogout: propTypes.func.isRequired
};

export { MobileMenu, MobileMenu as default };
